// Upper bound on how long a submit waits for the address. Past it the
// application still goes out, to the API's default recipient.
const RECIPIENT_TIMEOUT_MS = 5000

/**
 * Asks the app for the current job-application recipient right before a
 * submit, instead of baking it into the cached job page (END-318) — so an
 * editor's change in Umbraco applies to the very next application.
 *
 * Returns undefined on any failure; the caller then omits
 * `RecipientEmailAddress` and the API falls back to its configured address.
 */
export async function fetchJobApplicationRecipient(
  locale: string,
): Promise<string | undefined> {
  // AbortController + setTimeout rather than AbortSignal.timeout, which is
  // missing on Safari < 16 and would silently force the fallback there.
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), RECIPIENT_TIMEOUT_MS)
  try {
    const response = await fetch(
      `/api/careers/recipient?locale=${encodeURIComponent(locale)}`,
      { cache: "no-store", signal: controller.signal },
    )
    if (!response.ok) return undefined

    const { recipientEmail } = await response.json()
    return typeof recipientEmail === "string" && recipientEmail
      ? recipientEmail
      : undefined
  } catch (error) {
    console.error("Failed to fetch job application recipient:", error)
    return undefined
  } finally {
    clearTimeout(timer)
  }
}
